import MidiEffect from '../MidiEffect.js';
import ParamMappable from './ParamMappable.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {setEffectParameterValues} from 'actions/index.js';
import {FORWARD, FORWARD_REVERSE, FORWARD_STAY} from 'constants/accumulator-modes.js';
import ParamDisableable from "effects/midi/mappable/ParamDisableable.js";
import NodeMessaging from "messaging/NodeMessaging.js";

class AccumulatorEffect extends ParamDisableable(ParamMappable(NodeMessaging(MidiEffect))) {
    constructor(track) {
        super('Accumulator', track);

        this.storedAccumulator = 0;

        this.accumulatorValueParameter =  new AbstractParameter(
            'accumulatorValue', this, (o, x) => x,
            {
                documentation: "Adds this value to the accumulator on each trigger",
                min: -64,
                max: 64, 
                unit: '', 
                defaultValue: 1,
            }
        );

        this.registerParameter(this.accumulatorValueParameter);

        this.limitParameter =  new AbstractParameter(
            'limit', this, (o, x) => x,
            {
                documentation: "Accumulates until limit before resettig",
                min: -64,
                max: 64, 
                unit: '', 
                defaultValue: 64,
            }
        );

        this.registerParameter(this.limitParameter);

        this.syncedParameter = new AbstractParameter(
            "synced", this, (o,x) => x,
            {
                documentation: "Takes the trigger parameter and adds it to the accumulator value",
                min: 0,
                max: 1,
                defaultValue: null
            });

        this.registerParameter(this.syncedParameter);

        this.setupRampParameters();

        this.modeParameter = new AbstractParameter(
            "mode", this, (o,x) => x,
            {
                documentation: "How accumulator should act once it reaches the limit",
                min: 0,
                max: 1,
                defaultValue: FORWARD
            });

        this.registerParameter(this.modeParameter);

        this.directionFactor = 1;

        this.setupNodeMessaging();
    }

    getVisualizedParameters() {
        return [this.accumulatorValueParameter];
    }

    getText() {
        let value = this.accumulatorValue;
        if (value > 0) {
            return "+" + value;
        }
        return value;
    }

    clear() {
        // super.clear();
        this.directionFactor = 1;
        this.storedAccumulator = 0;

        this._clear();
    }

    clearParameter(parameter, i=this._parameters.indexOf(parameter)) {
        this.directionFactor = 1;
        this.storedAccumulator = 0;
        this._clearParameter(parameter, i);
    }

    get accumulatorValue() {
        return this.accumulatorValueParameter.get();
    }

    get mode() {
        return this.modeParameter.get();
    }

    get limit() {
        return this.limitParameter.get();
    }

    output(time, stepNumber) {
        let triggers = [... this.triggers];
        if (!this.track.sequencerEngine.playing || this.shouldOutputSkip(time, stepNumber)) {
            return triggers;
        }

        let trigger = this.triggers[0];
        let accumulatorValue = this.accumulatorValue;
        
        if (this.syncedParameter.get()) {
            let parameterValue = trigger.stepData[this.syncedParameter.get()];
            accumulatorValue += parameterValue;
        }

        // if there are triggers, then we need to call the accumulator
        this.storedAccumulator = (this.storedAccumulator + accumulatorValue*this.directionFactor);
        if (this.storedAccumulator >= this.limit) {
            if (this.mode === FORWARD) {
                this.storedAccumulator = this.storedAccumulator % this.limit;
            } else if (this.mode === FORWARD_REVERSE) {
                this.storedAccumulator = this.limit;
                this.directionFactor *= -1;
            }else if (this.mode === FORWARD_STAY) {
                this.storedAccumulator = this.limit;
            }
        } else if (Math.abs(this.storedAccumulator) >= this.limit) {
            if (this.mode === FORWARD) {
                this.storedAccumulator = ((this.storedAccumulator%this.limit)+this.limit)%this.limit;
            } else if (this.mode === FORWARD_REVERSE) {
                this.storedAccumulator = this.limit * -1;
                this.directionFactor *= -1;
            }else if (this.mode === FORWARD_STAY) {
                this.storedAccumulator = this.accumulatorValue < 0 ? -1 * this.limit : this.limit;
            }
        } else if (Math.abs(this.storedAccumulator) < Math.abs(this.accumulatorValue) && this.directionFactor === -1) {
            if (this.mode === FORWARD_REVERSE) {
                this.storedAccumulator = 0;
                this.directionFactor *= -1;
            }
        }

        this.setParameterValues(time);
        return this.triggers;
    }

    getParameterValueToSet(i) {
        return this.initialParameterValues[i] + this.storedAccumulator * (this.getDepth(i) / 100.0);
    }

    getCopy(track, ignoreId=true) {
        let effect = new AccumulatorEffect(track);
        effect.fromJson(this.getJson(), ignoreId);
        return effect;
    }
    
    randomlyMapParameters(numParameters=15) {
        let parameters = super.randomlyMapParameters(numParameters);

        let max = -4;
        for (let p of parameters) {
            if (p.max >= max) {
                max = p.max;
            }
        }

        this.accumulatorValueParameter.set(Math.random() * max / 10.0);
        this.limitParameter.set(max);

        // set the depths such that they actually make sense
        let i =0;
        for (let p of parameters) {
            let depth = 100.0 * p.max / max;

            // randomly lower the depth by some amount
            depth *= Math.random();

            this.depthParameters[i].set(depth);
            i++;
        }
    }
}

export default AccumulatorEffect;
